import React, { useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import {
  CircularProgress,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material'

import { apiZodios } from '~api/apiZodios'
import {
  Banner,
  Breadcrumbs,
  Button,
  Chip,
  ConfirmDeleteModal,
  Header,
  Icon,
  IconButton,
  PageLayout,
  type SemanticIconName,
  Toast,
  Typography,
} from '~components/internal'
import { type RootState } from '~redux/rootReducer'
import { type EnrichedOrganizationAISettings, type EnrichedPageSource } from '~types'

import { useAISettings } from '../../AISettingsContext'
import { pageTypeIconMap } from '../../constants/pageTypeIcons'

import './AIPageSourceViewerPage.scss'
import ChunkCard from './ChunkCard'

type SnackbarSeverity = 'success' | 'error'

interface SnackbarState {
  open: boolean
  message: string
  severity: SnackbarSeverity
}

const MARKDOWN_VIEW_TABS = [
  { label: 'Raw', value: 'raw' },
  { label: 'Rendered', value: 'rendered' },
] as const

const CHUNKING_SKIP_REASON_MESSAGES: Record<string, string> = {
  spreadsheet_file: 'This document is a spreadsheet and cannot be chunked for search.',
  table_heavy: 'This document is mostly tabular data and cannot be chunked for search.',
  ocr_errors: "This document has severe OCR corruption and couldn't be read.",
  document_too_large: 'This document is too large to process.',
}

type BannerState = { type: 'info' | 'error'; message: string } | null

function getChunkingBanner(
  chunkingStatus: string | null | undefined,
  chunkingSkipReason: string | null | undefined,
  chunkCount: number,
): BannerState {
  if (chunkingStatus === 'pending') {
    return {
      type: 'info',
      message:
        chunkCount > 0
          ? 'Chunking is in progress. Existing chunks may be replaced once complete.'
          : 'Chunking is in progress. Chunks will appear once complete.',
    }
  }
  if (chunkingSkipReason) {
    return {
      type: 'info',
      message:
        CHUNKING_SKIP_REASON_MESSAGES[chunkingSkipReason] ??
        'This document was skipped during chunking.',
    }
  }
  if (chunkingStatus === 'failed') {
    return {
      type: 'error',
      message:
        chunkCount > 0
          ? 'Chunking failed. Existing chunks may be outdated.'
          : 'Chunking failed for this document.',
    }
  }
  if (chunkCount === 0) {
    return { type: 'info', message: 'No chunks found for this source.' }
  }
  return null
}

function getExtractionBanner(
  extractionStatus: string | null | undefined,
  entityCount: number,
): BannerState {
  if (extractionStatus === 'pending') {
    return {
      type: 'info',
      message:
        entityCount > 0
          ? 'Extraction is in progress. Existing entities may be replaced once complete.'
          : 'Extraction is in progress. Entities will appear once complete.',
    }
  }
  if (extractionStatus === 'failed') {
    return {
      type: 'error',
      message:
        entityCount > 0
          ? 'Extraction failed. Existing entities may be outdated.'
          : 'Extraction failed for this document.',
    }
  }
  if (entityCount === 0) {
    return { type: 'info', message: 'No entities extracted from this source.' }
  }
  return null
}

const AIPageSourceViewerPageContent: React.FC = () => {
  const { pageSourceId, organizationHandle } = useParams<{
    pageSourceId: string
    organizationHandle: string
  }>()
  const navigate = useNavigate()

  const userId = useSelector((state: RootState) => state?.auth.userId)
  const isSuper = useSelector((state: RootState) => state?.auth?.user?.super)
  const { selectedOrganization } = useAISettings()

  const [orgAiSettings, setOrgAiSettings] = useState<EnrichedOrganizationAISettings | null>(null)
  const [pageSource, setPageSource] = useState<EnrichedPageSource | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [markdownView, setMarkdownView] = useState<'raw' | 'rendered'>('raw')
  const [rightPanelTab, setRightPanelTab] = useState<'chunks' | 'entities'>('chunks')
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [rechunking, setRechunking] = useState<boolean>(false)
  const [rechunkDialogOpen, setRechunkDialogOpen] = useState<boolean>(false)
  const [reextracting, setReextracting] = useState<boolean>(false)
  const [reextractDialogOpen, setReextractDialogOpen] = useState<boolean>(false)
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  })
  const urlMenuTriggerRef = useRef<HTMLButtonElement>(null)
  const [urlMenuOpen, setUrlMenuOpen] = useState<boolean>(false)

  const showSnackbar = (message: string, severity: SnackbarSeverity = 'success'): void => {
    setSnackbar({ open: true, message, severity })
  }

  const fetchOrgAiSettings = async (): Promise<void> => {
    if (!userId || !selectedOrganization?.organization_id) return
    try {
      const response = await apiZodios.ai_initializeAI({
        user_id: userId,
        organization_id: selectedOrganization.organization_id,
      })
      setOrgAiSettings(response.organization_ai_settings || null)
    } catch (err) {
      console.error('Error fetching organization AI settings:', err)
    }
  }

  const fetchPageSource = async (): Promise<void> => {
    if (!pageSourceId || !userId || !selectedOrganization?.id) return

    setLoading(true)
    setError(null)

    try {
      const response = await apiZodios.ai_getPageSource({
        user_id: userId,
        organization_identity_id: selectedOrganization.id,
        page_source_id: pageSourceId,
      })
      setPageSource(response.page_source || null)
    } catch (err) {
      setError(`Failed to fetch page source: ${err}`)
      console.error('Error fetching page source:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!pageSourceId || !userId || !selectedOrganization) return
    fetchOrgAiSettings()
    fetchPageSource()
  }, [pageSourceId, userId, selectedOrganization])

  const handleBack = (): void => {
    navigate(`/organizations/${organizationHandle}/sources`)
  }

  const handleArchive = async (): Promise<void> => {
    if (!pageSource || !userId || !selectedOrganization?.id) return
    try {
      await apiZodios.ai_editPageSource({
        user_id: userId,
        organization_identity_id: selectedOrganization.id,
        page_id: pageSource.page_source_id,
        archived: true,
      })
      handleBack()
    } catch (err) {
      console.error('Error archiving page:', err)
      setError('An error occurred while archiving the page')
    }
  }

  const handleUnarchive = async (): Promise<void> => {
    if (!pageSource || !userId || !selectedOrganization?.id) return
    try {
      await apiZodios.ai_editPageSource({
        user_id: userId,
        organization_identity_id: selectedOrganization.id,
        page_id: pageSource.page_source_id,
        archived: false,
      })
      handleBack()
    } catch (err) {
      console.error('Error unarchiving page:', err)
      setError('An error occurred while unarchiving the page')
    }
  }

  const handleCopy = async (text: string, id: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedId(id)
      setTimeout(() => setCopiedId(null), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const handleRechunkConfirm = async (): Promise<void> => {
    if (!userId || !selectedOrganization?.id || !pageSource) return
    setRechunkDialogOpen(false)
    setRechunking(true)
    try {
      await apiZodios.ai_rechunkPageSource({
        user_id: userId,
        organization_identity_id: selectedOrganization.id,
        page_source_id: pageSource.page_source_id,
      })
      handleBack()
    } catch (err) {
      console.error('Failed to re-chunk:', err)
      showSnackbar('Failed to queue re-chunk.', 'error')
      setRechunking(false)
    }
  }

  const handleReextractConfirm = async (): Promise<void> => {
    if (!userId || !selectedOrganization?.id || !pageSource) return
    setReextractDialogOpen(false)
    setReextracting(true)
    try {
      await apiZodios.ai_reextractPageSource({
        user_id: userId,
        organization_identity_id: selectedOrganization.id,
        page_source_id: pageSource.page_source_id,
      })
      showSnackbar('Re-extraction queued.')
      setReextracting(false)
      fetchPageSource()
    } catch (err) {
      console.error('Failed to re-extract:', err)
      showSnackbar('Failed to queue re-extraction.', 'error')
      setReextracting(false)
    }
  }

  const handleChunkDeleted = (chunkId: string): void => {
    setPageSource((prev) =>
      prev ? { ...prev, chunks: prev.chunks.filter((c) => c.chunk_id !== chunkId) } : prev,
    )
  }

  if (loading) {
    return (
      <PageLayout>
        <div className='page-source-viewer__loading'>
          <CircularProgress size={40} />
          <Typography variant='body1'>Loading page source...</Typography>
        </div>
      </PageLayout>
    )
  }

  if (error || !pageSource) {
    return (
      <PageLayout
        breadcrumb={
          <Breadcrumbs
            items={[{ label: 'Knowledge Base', onClick: handleBack }, { label: 'Page source' }]}
          />
        }
      >
        <Banner
          type='error'
          title='Unable to load page source'
          body={error || 'Page source not found'}
        />
        <div className='page-source-viewer__error-actions'>
          <Button variant='outlined' onClick={handleBack}>
            Go back
          </Button>
        </div>
      </PageLayout>
    )
  }

  const pageTypeIcon = pageSource.page_type
    ? (pageTypeIconMap[pageSource.page_type] ?? pageTypeIconMap['Unknown'])
    : null

  const chunkCount = pageSource.chunks.length
  const chunkingBanner = getChunkingBanner(
    pageSource.chunking_status,
    pageSource.chunking_skip_reason,
    chunkCount,
  )

  const entities = pageSource.extracted_entities ?? []
  const entityCount = entities.length
  const extractionBanner = getExtractionBanner(pageSource.extraction_status, entityCount)

  const entityGroups = entities.reduce<Record<string, typeof entities>>((groups, entity) => {
    const type = entity.entity_type
    if (!groups[type]) groups[type] = []
    groups[type].push(entity)
    return groups
  }, {})

  const handleCopyUrl = (): void => {
    setUrlMenuOpen(false)
    if (pageSource.url) {
      void handleCopy(pageSource.url, 'page-url')
    }
  }

  const handleCopyPageId = (): void => {
    setUrlMenuOpen(false)
    void handleCopy(pageSource.page_source_id, 'page-id')
  }

  const urlCopiedRecently = copiedId === 'page-url' || copiedId === 'page-id'

  const archiveButton = pageSource.archived ? (
    <Tooltip title='Restore this source to Search'>
      <span>
        <Button variant='contained' startIcon={<Icon name='unarchive' />} onClick={handleUnarchive}>
          Unarchive
        </Button>
      </span>
    </Tooltip>
  ) : (
    <Tooltip title='Exclude this source from Search'>
      <span>
        <Button variant='contained' startIcon={<Icon name='archive' />} onClick={handleArchive}>
          Archive
        </Button>
      </span>
    </Tooltip>
  )

  const tabActionButton =
    isSuper && !pageSource.archived ? (
      rightPanelTab === 'chunks' ? (
        <Button
          variant='outlined'
          size='small'
          startIcon={<Icon name='refresh' />}
          disabled={!pageSource.markdown || rechunking}
          onClick={() => setRechunkDialogOpen(true)}
        >
          Re-chunk
        </Button>
      ) : (
        <Button
          variant='outlined'
          size='small'
          startIcon={<Icon name='refresh' />}
          disabled={!pageSource.markdown || reextracting}
          onClick={() => setReextractDialogOpen(true)}
        >
          Re-extract
        </Button>
      )
    ) : undefined

  const headerTitleAddon =
    pageSource.page_type && pageTypeIcon ? (
      <div className='page-source-viewer__title-addon'>
        <Chip
          color='neutral'
          icon={<Icon name={pageTypeIcon as SemanticIconName} />}
          label={pageSource.page_type}
        />
      </div>
    ) : undefined

  return (
    <PageLayout
      breadcrumb={
        <Breadcrumbs
          items={[
            { label: 'Knowledge Base', onClick: handleBack },
            { label: pageSource.title || 'Page source' },
          ]}
        />
      }
      header={
        <Header
          title={pageSource.title}
          headerIcon={
            orgAiSettings?.organization_logo && orgAiSettings?.organization_name ? (
              <img
                src={orgAiSettings.organization_logo}
                alt={`${orgAiSettings.organization_name} logo`}
                className='page-source-viewer__org-logo'
              />
            ) : undefined
          }
          headerTitleAddon={headerTitleAddon}
          primaryButton={archiveButton}
        />
      }
    >
      {pageSource.url && (
        <div className='page-source-viewer__url-row'>
          <Typography variant='body1' color='textSecondary' className='page-source-viewer__url'>
            {pageSource.url}
          </Typography>
          <Tooltip title='Copy options'>
            <span>
              <IconButton
                ref={urlMenuTriggerRef}
                size='small'
                onClick={() => setUrlMenuOpen(true)}
                aria-haspopup='menu'
                aria-expanded={urlMenuOpen}
              >
                <Icon name='copy' fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
          {urlCopiedRecently && (
            <Typography variant='caption' color='success.main'>
              Copied!
            </Typography>
          )}
          <Menu
            anchorEl={urlMenuTriggerRef.current}
            open={urlMenuOpen}
            onClose={() => setUrlMenuOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={handleCopyUrl}>Copy URL</MenuItem>
            <MenuItem onClick={handleCopyPageId}>Copy Page Source ID</MenuItem>
          </Menu>
        </div>
      )}

      <div className='page-source-viewer__content'>
        <section className='page-source-viewer__content-column'>
          <div className='page-source-viewer__content-column-header'>
            <div className='page-source-viewer__panel-title-group'>
              <Typography variant='h4'>Content Preview</Typography>
              {pageSource.markdown && (
                <Tooltip title='Copy Markdown'>
                  <IconButton
                    size='small'
                    onClick={() => handleCopy(pageSource.markdown!, 'markdown-content')}
                  >
                    <Icon name='copy' fontSize='small' />
                  </IconButton>
                </Tooltip>
              )}
              {copiedId === 'markdown-content' && (
                <Typography variant='caption' color='success.main'>
                  Copied!
                </Typography>
              )}
            </div>
            <ToggleButtonGroup
              value={markdownView}
              exclusive
              onChange={(_e, val) => val && setMarkdownView(val as 'raw' | 'rendered')}
              className='page-source-viewer__toggle-group'
            >
              {MARKDOWN_VIEW_TABS.map((tab) => (
                <ToggleButton key={tab.value} value={tab.value}>
                  {tab.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </div>

          <div className='page-source-viewer__viewer'>
            {pageSource.markdown ? (
              markdownView === 'raw' ? (
                <pre className='page-source-viewer__markdown-raw'>{pageSource.markdown}</pre>
              ) : (
                <div className='page-source-viewer__markdown-rendered'>
                  <ReactMarkdown>{pageSource.markdown}</ReactMarkdown>
                </div>
              )
            ) : (
              <Typography variant='body2' color='textSecondary'>
                No markdown content available
              </Typography>
            )}
          </div>
        </section>

        <section className='page-source-viewer__content-column'>
          <div className='page-source-viewer__content-column-header'>
            <ToggleButtonGroup
              value={rightPanelTab}
              exclusive
              onChange={(_e, val) => val && setRightPanelTab(val as 'chunks' | 'entities')}
              className='page-source-viewer__toggle-group'
            >
              <ToggleButton value='chunks'>Chunks ({chunkCount})</ToggleButton>
              <ToggleButton value='entities'>Entities ({entityCount})</ToggleButton>
            </ToggleButtonGroup>
            {tabActionButton}
          </div>

          <div className='page-source-viewer__viewer page-source-viewer__viewer--scroll'>
            {rightPanelTab === 'chunks' && (
              <>
                {chunkingBanner && (
                  <Banner type={chunkingBanner.type} body={chunkingBanner.message} />
                )}
                {chunkCount > 0 && (
                  <div className='page-source-viewer__chunks'>
                    {pageSource.chunks.map((chunk, index) => (
                      <ChunkCard
                        key={chunk.chunk_id}
                        chunk={chunk}
                        index={index}
                        onCopy={handleCopy}
                        copiedId={copiedId}
                        organizationIdentityId={selectedOrganization!.id}
                        onDeleted={handleChunkDeleted}
                      />
                    ))}
                  </div>
                )}
              </>
            )}

            {rightPanelTab === 'entities' && (
              <>
                {extractionBanner && (
                  <Banner type={extractionBanner.type} body={extractionBanner.message} />
                )}
                {entityCount > 0 && (
                  <div className='page-source-viewer__entities'>
                    {Object.entries(entityGroups).map(([type, groupEntities]) => (
                      <div key={type} className='page-source-viewer__entity-group'>
                        <div className='page-source-viewer__entity-group-header'>
                          <Typography
                            variant='h6'
                            className='page-source-viewer__entity-type-label'
                          >
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </Typography>
                          <Chip color='neutral' label={String(groupEntities.length)} />
                        </div>
                        {groupEntities.map((entity) => (
                          <div key={entity.entity_id} className='page-source-viewer__entity-card'>
                            <Typography variant='subtitle2'>
                              {entity.name ?? '(unnamed)'}
                            </Typography>
                            {(entity.start_date || entity.end_date) && (
                              <Typography
                                variant='caption'
                                color='textSecondary'
                                className='page-source-viewer__entity-dates'
                              >
                                {entity.start_date && `From: ${entity.start_date}`}
                                {entity.start_date && entity.end_date && ' · '}
                                {entity.end_date && `To: ${entity.end_date}`}
                              </Typography>
                            )}
                            {Object.entries(entity.attributes).length > 0 && (
                              <dl className='page-source-viewer__entity-attributes'>
                                {Object.entries(entity.attributes).map(([key, value]) => (
                                  <div
                                    key={key}
                                    className='page-source-viewer__entity-attribute-row'
                                  >
                                    <dt>
                                      <Typography variant='caption' color='textSecondary'>
                                        {key}
                                      </Typography>
                                    </dt>
                                    <dd>
                                      <Typography variant='caption'>
                                        {value != null ? String(value) : '—'}
                                      </Typography>
                                    </dd>
                                  </div>
                                ))}
                              </dl>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>

      <ConfirmDeleteModal
        isOpen={rechunkDialogOpen}
        onClose={() => setRechunkDialogOpen(false)}
        onConfirm={handleRechunkConfirm}
        title='Re-chunk this page?'
        description='This will delete and regenerate all chunks. This cannot be undone.'
      />

      <ConfirmDeleteModal
        isOpen={reextractDialogOpen}
        onClose={() => setReextractDialogOpen(false)}
        onConfirm={handleReextractConfirm}
        title='Re-extract entities for this page?'
        description='This will delete and regenerate all extracted entities for this page.'
      />

      <Toast
        type={snackbar.severity}
        message={snackbar.message}
        open={snackbar.open}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </PageLayout>
  )
}

const AIPageSourceViewerPage: React.FC = () => {
  return <AIPageSourceViewerPageContent />
}

export default AIPageSourceViewerPage
